import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { API_URL } from '../../api';

const CATEGORY_LABELS = {
    'path-to-the-robe': 'The Path to the Robe',
    'as-it-was-taught-to-me': 'As It Was Taught to Me',
};

const formatDate = (isoString) => {
    if (!isoString) return '';
    try {
        return new Date(isoString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    } catch {
        return isoString;
    }
};

const authorDisplay = (author) => (author === 'brandon' ? 'Brandon' : 'Logan');

// Markdown component overrides for dark theme (no @tailwindcss/typography needed)
const markdownComponents = {
    h1: ({ children }) => (
        <h1 className="text-2xl font-bold text-white mt-8 mb-4 leading-tight">{children}</h1>
    ),
    h2: ({ children }) => (
        <h2 className="text-xl font-bold text-white mt-7 mb-3 leading-tight">{children}</h2>
    ),
    h3: ({ children }) => (
        <h3 className="text-lg font-semibold text-white mt-6 mb-3">{children}</h3>
    ),
    h4: ({ children }) => (
        <h4 className="text-base font-semibold text-gray-200 mt-5 mb-2">{children}</h4>
    ),
    p: ({ children }) => (
        <p className="text-gray-300 leading-relaxed mb-4">{children}</p>
    ),
    ul: ({ children }) => (
        <ul className="list-disc list-inside text-gray-300 mb-4 space-y-1 pl-4">{children}</ul>
    ),
    ol: ({ children }) => (
        <ol className="list-decimal list-inside text-gray-300 mb-4 space-y-1 pl-4">{children}</ol>
    ),
    li: ({ children }) => (
        <li className="text-gray-300 leading-relaxed">{children}</li>
    ),
    blockquote: ({ children }) => (
        <blockquote className="border-l-4 border-amber-500 pl-4 my-4 italic text-gray-400">
            {children}
        </blockquote>
    ),
    code: ({ inline, children }) =>
        inline ? (
            <code className="bg-slate-700 text-amber-300 px-1 py-0.5 rounded text-sm font-mono">
                {children}
            </code>
        ) : (
            <code className="block bg-slate-800 text-gray-300 p-4 rounded-lg text-sm font-mono overflow-x-auto mb-4 whitespace-pre">
                {children}
            </code>
        ),
    pre: ({ children }) => (
        <pre className="bg-slate-800 rounded-lg overflow-x-auto mb-4">{children}</pre>
    ),
    a: ({ href, children }) => (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-400 hover:underline"
        >
            {children}
        </a>
    ),
    hr: () => <hr className="border-slate-600 my-6" />,
    strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
    em: ({ children }) => <em className="text-gray-300 italic">{children}</em>,
};

const BlogPost = () => {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPost = async () => {
            setIsLoading(true);
            setNotFound(false);
            setError(null);
            try {
                const res = await fetch(`${API_URL}/blog/${slug}`);
                if (res.status === 404) {
                    setNotFound(true);
                    return;
                }
                if (!res.ok) throw new Error(`Request failed (${res.status})`);
                const data = await res.json();
                setPost(data.post || data);
            } catch (err) {
                setError('Unable to load this post at this time.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchPost();
    }, [slug]);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-10 h-10 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" aria-label="Loading" />
            </div>
        );
    }

    if (notFound) {
        return (
            <div className="min-h-screen py-12 px-6">
                <div className="max-w-3xl mx-auto">
                    <nav className="mb-6 text-sm text-gray-400" aria-label="Breadcrumb">
                        <Link to="/blog" className="hover:text-amber-400 transition-colors">Blog</Link>
                        <span className="mx-2 text-gray-600">&rsaquo;</span>
                        <span className="text-gray-200">Post Not Found</span>
                    </nav>
                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-10 text-center">
                        <p className="text-white text-lg mb-4">Post not found.</p>
                        <Link to="/blog" className="text-amber-400 hover:underline text-sm">
                            Back to Blog
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen py-12 px-6">
                <div className="max-w-3xl mx-auto">
                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-10 text-center">
                        <p className="text-gray-400">{error}</p>
                        <Link to="/blog" className="mt-4 inline-block text-amber-400 hover:underline text-sm">
                            Back to Blog
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const categoryLabel = CATEGORY_LABELS[post?.category] || post?.category;

    return (
        <div className="min-h-screen py-12 px-6">
            <div className="max-w-3xl mx-auto">
                {/* Breadcrumb */}
                <nav className="mb-6 text-sm text-gray-400" aria-label="Breadcrumb">
                    <Link to="/blog" className="hover:text-amber-400 transition-colors">Blog</Link>
                    {categoryLabel && (
                        <>
                            <span className="mx-2 text-gray-600">&rsaquo;</span>
                            <Link
                                to={`/blog/${post?.category}`}
                                className="hover:text-amber-400 transition-colors"
                            >
                                {categoryLabel}
                            </Link>
                        </>
                    )}
                    <span className="mx-2 text-gray-600">&rsaquo;</span>
                    <span className="text-gray-200 line-clamp-1">{post?.title}</span>
                </nav>

                <article className="bg-slate-900/70 backdrop-blur-sm border border-slate-700 rounded-xl p-7 md:p-10">
                    <header className="mb-8 pb-6 border-b border-slate-700">
                        <h1 className="text-3xl font-bold text-white leading-tight mb-3">
                            {post?.title}
                        </h1>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400">
                            <span>By {authorDisplay(post?.author)}</span>
                            {categoryLabel && (
                                <>
                                    <span>&middot;</span>
                                    <Link
                                        to={`/blog/${post?.category}`}
                                        className="text-amber-400 hover:underline"
                                    >
                                        {categoryLabel}
                                    </Link>
                                </>
                            )}
                            {(post?.publishedAt || post?.createdAt) && (
                                <>
                                    <span>&middot;</span>
                                    <span>{formatDate(post.publishedAt || post.createdAt)}</span>
                                </>
                            )}
                        </div>
                    </header>

                    <div className="mt-6">
                        {post?.content ? (
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={markdownComponents}
                            >
                                {post.content}
                            </ReactMarkdown>
                        ) : post?.excerpt ? (
                            <p className="text-gray-300 leading-relaxed">{post.excerpt}</p>
                        ) : null}
                    </div>
                </article>

                <div className="mt-6">
                    <Link
                        to={post?.category ? `/blog/${post.category}` : '/blog'}
                        className="text-sm text-amber-400 hover:text-amber-300 transition-colors"
                    >
                        &larr; Back to {categoryLabel || 'Blog'}
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default BlogPost;
